import { isAuthError, isAuthRetryableFetchError } from '@supabase/supabase-js';
import NetInfo from '@react-native-community/netinfo';
import * as Sentry from '@sentry/react-native';
import { supabase } from '../data/client';

// Global singleton: ensures only ONE refreshSession() call is in flight at any
// time across the entire app. Supabase GoTrue has refresh-token reuse detection
// — two concurrent refreshes using the same refresh token revoke the entire
// session family → SIGNED_OUT → random logouts. Every manual refreshSession()
// call in the app must route through this function instead of calling
// supabase.auth.refreshSession() directly.
let _refreshPromise = null;

export const ensureFreshSession = async () => {
    // E2E contexts share one storage-state session across parallel workers —
    // a manual refresh would consume the single-use refresh token and trip
    // Supabase's reuse detection, revoking the family for every context.
    if (typeof window !== 'undefined' && window.__E2E_DISABLE_SESSION_REFRESH) {
        return supabase.auth.getSession();
    }
    if (_refreshPromise) return _refreshPromise;
    _refreshPromise = supabase.auth.refreshSession().finally(() => {
        _refreshPromise = null;
    });
    return _refreshPromise;
};

// Bounds a single getSession() attempt. A stalled connection can hang the auth
// fetch far longer than users will wait on a splash screen; the attempt is
// abandoned (the underlying refresh continues inside auth-js and later attempts
// join it) so the retry loop stays responsive.
const SESSION_ATTEMPT_TIMEOUT_MS = 20 * 1000;
const RETRY_BASE_DELAY_MS = 3000;
const RETRY_MAX_DELAY_MS = 15000;
const CONNECTIVITY_POLL_MS = 5000;

/**
 * Classifies a getSession()/refresh error as transient (safe to retry, session
 * is NOT proof of being logged out) vs genuine (session is really gone).
 *
 * auth-js behavior (verified in @supabase/auth-js 2.112.3):
 * - AuthRetryableFetchError (network failure, abort, HTTP 500-504/520-530):
 *   session is left in storage, no SIGNED_OUT — getSession just returns
 *   { session: null, error }. MUST be retried, not treated as logged out.
 * - AuthApiError / AuthSessionMissingError (invalid_grant, session_not_found):
 *   refresh token genuinely dead — auth-js removes the session and emits
 *   SIGNED_OUT. Correct to treat as logged out.
 * - AuthUnknownError (non-JSON error body at a non-5xx status, e.g. a captive
 *   portal or WAF block page): ambiguous — retry instead of kicking.
 * - Non-Auth thrown errors (timeout, aborted lock, storage read failure):
 *   transient — they say nothing about session validity.
 */
export const isTransientSessionError = (error) => {
    if (!error) return false;
    if (isAuthRetryableFetchError(error)) return true;
    if (!isAuthError(error)) return true;
    return error.name === 'AuthUnknownError';
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Sleep that wakes early when shouldAbort() flips true — a user-initiated
// sign out during the retry backoff shouldn't wait out the full delay.
const abortableSleep = async (ms, shouldAbort) => {
    const end = Date.now() + ms;
    while (Date.now() < end) {
        if (shouldAbort?.()) return;
        await sleep(Math.min(250, end - Date.now()));
    }
};

// Waits until NetInfo reports connectivity (isConnected !== false — null means
// "unknown", which we treat as connected rather than blocking on it). Listens
// for change events and polls as a backstop since the listener can miss a
// transition during cold start.
const waitForConnectivity = (shouldAbort) => new Promise((resolve) => {
    let settled = false;
    const finish = () => {
        if (settled) return;
        settled = true;
        unsubscribe();
        clearInterval(interval);
        resolve();
    };
    const check = async () => {
        if (shouldAbort?.()) return finish();
        try {
            const state = await NetInfo.fetch();
            if (state.isConnected !== false) finish();
        } catch {
            // NetInfo read failed — try again on next tick.
        }
    };
    const unsubscribe = NetInfo.addEventListener(check);
    const interval = setInterval(check, CONNECTIVITY_POLL_MS);
});

const withAttemptTimeout = (promise, ms) =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(`Session restore attempt timed out after ${ms}ms`)), ms)),
    ]);

/**
 * Resilient getSession() for auth gates (root layout, tabs layout, login).
 *
 * Returns the restored Session, or null ONLY when the session is genuinely
 * absent/invalid (nothing persisted, or a non-retryable auth error such as a
 * revoked refresh token). Transient failures (offline, slow network, auth
 * endpoint 5xx, hung fetch) never produce null — the call keeps retrying:
 *
 * - While offline (NetInfo isConnected === false): waits for connectivity to
 *   return rather than hammering a dead network.
 * - While connected but failing: exponential-ish backoff (3s..15s cap).
 * - Pass maxAttempts to bound the loop (login screen, where falling through
 *   to the form is acceptable).
 *
 * @param {object} [options]
 * @param {(attempt: number, reason: 'offline'|'retrying') => void} [options.onRetry]
 *        Called when a retry wait begins — use it to show "connection slow" UI.
 * @param {() => boolean} [options.shouldAbort] - Return true to stop and get null.
 * @param {number} [options.maxAttempts] - Cap on failed attempts before returning null.
 * @returns {Promise<import('@supabase/supabase-js').Session|null>}
 */
export const restoreSessionWithRetry = async ({ onRetry, shouldAbort, maxAttempts } = {}) => {
    let attempt = 0;

    while (true) {
        if (shouldAbort?.()) return null;

        // Always attempt first: a session whose access token is still valid
        // restores from storage with NO network at all — don't gate it behind
        // connectivity, or an offline user with a live token waits forever.
        try {
            const { data, error } = await withAttemptTimeout(supabase.auth.getSession(), SESSION_ATTEMPT_TIMEOUT_MS);

            if (data?.session) return data.session;

            // { session: null, error: null } — nothing persisted / session was
            // removed (invalid_grant also lands here on the next call after
            // auth-js clears storage). Genuinely logged out.
            if (!error) return null;

            if (!isTransientSessionError(error)) return null;

            if (attempt < 3 || attempt % 10 === 0) {
                Sentry.logger.warn('Session restore hit transient error — retrying', {
                    attempt: attempt + 1,
                    errorName: error?.name,
                    status: error?.status,
                    message: error?.message,
                });
            }
        } catch (thrown) {
            // getSession itself rejected (attempt timeout, storage/lock error).
            // Not evidence of logout — retry.
            if (attempt < 3 || attempt % 10 === 0) {
                Sentry.logger.warn('Session restore attempt threw — retrying', {
                    attempt: attempt + 1,
                    error: thrown,
                });
            }
        }

        attempt += 1;
        if (maxAttempts && attempt >= maxAttempts) return null;

        // Retry wait: while offline, sit on NetInfo until connectivity returns
        // instead of hammering a dead network; while connected, back off
        // (3s, 6s, … capped at 15s). Both wake early on abort (e.g. sign out).
        let offline = false;
        try {
            const net = await NetInfo.fetch();
            offline = net.isConnected === false;
        } catch {
            // NetInfo read failed — treat as connected and use plain backoff.
        }

        if (offline) {
            onRetry?.(attempt, 'offline');
            await waitForConnectivity(shouldAbort);
        } else {
            onRetry?.(attempt, 'retrying');
            await abortableSleep(Math.min(RETRY_BASE_DELAY_MS * attempt, RETRY_MAX_DELAY_MS), shouldAbort);
        }
    }
};
